/**
 * <s-multi-variant-picker>
 * When the buyer sets the product quantity to 2 or more, shows one row per
 * piece so each piece can get its own option values (e.g. color and size).
 * <s-product-form> reads getLines() on submit to add every piece to the cart.
 * Selections survive the product-form partial re-render via a per-product cache.
 */
const savedSelections = new Map();

class SMultiVariantPicker extends HTMLElement {
  #ac;
  #variants = [];
  #form;
  #rows;
  #template;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#form = this.closest('form');
    this.#rows = this.querySelector('[data-rows]');
    this.#template = this.querySelector('template[data-row-template]');
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    if (!this.#form || !this.#rows || !this.#template || !this.#variants.length) return;

    this.#form.addEventListener('s-quantity:change', () => this.#sync(), opts);
    this.#quantityInput()?.addEventListener('input', () => this.#sync(), opts);
    this.#rows.addEventListener('change', (event) => {
      const row = event.target.closest('[data-row]');
      if (row) {
        row.dataset.touched = "1";
        this.#updateRow(row);
      }
      this.#afterChange();
    }, opts);

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Returns null when inactive, false when a piece is invalid, else cart lines grouped by variant. */
  getLines() {
    if (this.hidden) return null;
    const counts = new Map();
    for (const row of this.#rows.querySelectorAll('[data-row]')) {
      const variant = this.#variantById(row.dataset.variantId);
      if (!variant || !variant.available) return false;
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    }
    return [...counts].map(([id, quantity]) => ({ merchandiseId: String(id), quantity }));
  }

  #quantityInput() {
    const input = this.#form?.elements.namedItem('quantity');
    return input instanceof HTMLInputElement ? input : null;
  }

  #mainVariantId() {
    const input = this.#form?.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  #variantById(id) {
    return this.#variants.find((variant) => String(variant.id) === String(id));
  }

  #sync() {
    const quantity = Number.parseInt(this.#quantityInput()?.value || '1', 10) || 1;
    const maxRows = Number.parseInt(this.dataset.maxRows || '10', 10);
    const active = quantity >= 2 && quantity <= maxRows;

    this.hidden = !active;
    if (!active) {
      this.#rows.replaceChildren();
      this.#afterChange();
      return;
    }

    const saved = savedSelections.get(this.dataset.productId) || [];
    const existing = this.#rows.querySelectorAll('[data-row]');
    for (let i = existing.length; i < quantity; i += 1) {
      const entry = saved[i];
      const row = this.#createRow(i, entry?.touched ? entry.variantId : this.#mainVariantId());
      if (entry?.touched) row.dataset.touched = "1";
      this.#rows.append(row);
    }
    [...this.#rows.querySelectorAll('[data-row]')].slice(quantity).forEach((row) => row.remove());
    this.#afterChange();
  }

  #createRow(index, variantId) {
    const fragment = this.#template.content.cloneNode(true);
    const row = fragment.querySelector('[data-row]');
    const label = row.querySelector('[data-row-label]');
    if (label) label.textContent = `${this.dataset.itemLabel || ''} ${index + 1}`.trim();

    const variant = this.#variantById(variantId) || this.#variants.find((v) => v.available) || this.#variants[0];
    row.querySelectorAll('select[data-option-index]').forEach((select) => {
      const position = Number(select.dataset.optionIndex);
      select.id = `${select.id}-${index}`;
      const selectLabel = row.querySelector(`label[data-for-option="${position}"]`);
      if (selectLabel) selectLabel.htmlFor = select.id;
      if (variant) select.value = variant.options[position];
    });
    this.#updateRow(row);
    return row;
  }

  #updateRow(row) {
    const values = [...row.querySelectorAll('select[data-option-index]')].map((select) => select.value);
    const variant = this.#variants.find((v) => v.options.every((value, i) => value === values[i]));
    row.dataset.variantId = variant ? String(variant.id) : '';
    const message = row.querySelector('[data-row-message]');
    const valid = Boolean(variant && variant.available);
    if (message) message.hidden = valid;
    row.toggleAttribute('data-invalid', !valid);
  }

  #afterChange() {
    const rows = [...this.#rows.querySelectorAll('[data-row]')];
    if (rows.length) savedSelections.set(this.dataset.productId, rows.map((row) => ({
      variantId: row.dataset.variantId,
      touched: row.dataset.touched === "1",
    })));

    // Buy it now only buys the main selection, so hide it while pieces differ.
    const mainId = this.#mainVariantId();
    const mixed = rows.some((row) => row.dataset.variantId !== mainId);
    const buyNow = this.#form?.querySelector('[data-buy-now]');
    if (buyNow) buyNow.hidden = !this.hidden && mixed;
  }
}

if (!customElements.get('s-multi-variant-picker')) {
  customElements.define('s-multi-variant-picker', SMultiVariantPicker);
}
